import { Typography } from '@tempmee/react';
import { CenteredSpinner , Box, Column } from '~/commonComponents';
import { colors } from '@tempmee/tailwind';
import { CSSProperties, useMemo } from 'react';
import { IDataGridProps } from './Types';
import { Styles } from './Constants';

export function DataGrid<T>({
  EmptyComponent,
  PaginationComponent,
  columns,
  data,
  dataCy,
  dataTestId,
  isLoading,
  isStickyHeader = false,
  showHeaderSeparator = true,
  showRowSeparator = true,
  getRowClassName,
  getRowStyle,
  keyExtractor,
  onRowClick,
}: IDataGridProps<T>) {
  const gridTemplateColumns = useMemo(
    () => columns.map((col) => col.width || '1fr').join(' '),
    [columns],
  );

  const getCellStyle = (align?: 'left' | 'center' | 'right'): CSSProperties => {
    if (!align || align === 'left') return {};
    return {
      display: 'flex',
      justifyContent: align,
      alignItems: 'center',
    };
  };

  if (!isLoading && data.length === 0) {
    return (
      <Column data-cy={dataCy?.root} data-testid={dataTestId?.root}>
        {EmptyComponent}
      </Column>
    );
  }

  return (
    <Column data-cy={dataCy?.root} data-testid={dataTestId?.root}>
      <Box
        data-cy={dataCy?.header}
        data-testid={dataTestId?.header}
        style={{
          ...Styles.Header,
          ...(isStickyHeader ? Styles.HeaderSticky : {}),
          ...(showHeaderSeparator && { borderBottom: `2px solid ${colors.neutral[100]}` }),
          gridTemplateColumns,
        }}
      >
        {columns.map((column) => (
          <Box key={column.key} style={getCellStyle(column.align)}>
            {typeof column.header === 'string' ? (
              <Typography as="span" bold color={colors.primary[500]} variant="body1">
                {column.header}
              </Typography>
            ) : (
              column.header
            )}
          </Box>
        ))}
      </Box>
      {isLoading ? (
        <Box marginTop={24}>
          <CenteredSpinner />
        </Box>
      ) : (
        <>
          <Column style={Styles.Body}>
            {data.map((item) => (
              <Box
                className={getRowClassName?.(item)}
                data-cy={dataCy?.rowItem}
                data-testid={dataTestId?.rowItem}
                key={keyExtractor(item)}
                style={{
                  ...Styles.BodyRowItem,
                  ...(showRowSeparator && { borderBottom: `2px solid ${colors.neutral[100]}` }),
                  cursor: onRowClick ? 'pointer' : 'default',
                  gridTemplateColumns,
                  ...getRowStyle?.(item),
                }}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map((column) => (
                  <Box key={column.key} style={getCellStyle(column.align)}>
                    {column.render(item)}
                  </Box>
                ))}
              </Box>
            ))}
          </Column>
          {PaginationComponent && <Column marginTop={24}>{PaginationComponent}</Column>}
        </>
      )}
    </Column>
  );
}
